import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useFiltersQuery, useSettingsQuery } from "@/lib/queries";
import type { AskOptions, Feature } from "@/types";
import { FEATURE_SPECS, LOCKED_FEATURES } from "@/types";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AdvancedOptions } from "@/components/ask/AdvancedOptions";
import { MEASURE, Page, PageHeader, PageScroll, Rail } from "@/components/layout/Page";
import { useMediaQuery, XL } from "@/lib/use-media-query";
import { ProcessingModal } from "@/components/ask/ProcessingModal";
import { MyRecentQuestions } from "@/components/ask/MyRecentQuestions";

export const Route = createFileRoute("/ask")({
  component: AskPage,
});

// Every feature the pipeline knows, in ERIKA's order. Derived from the specs
// rather than written out again, so a feature added to one list cannot go
// missing from the other.
// Everything but the citation checker, which ERIKA offered but never ran.
const DEFAULT_FEATURES: Feature[] = FEATURE_SPECS.map((f) => f.value).filter(
  (f) => f !== "citation_checker",
);

function AskPage() {
  const navigate = useNavigate();
  // Above `xl` the options are a rail beside the question rather than a panel
  // under it, and a rail that is shut is an empty column.
  const wide = useMediaQuery(XL);
  const filtersQuery = useFiltersQuery();
  const filters = filtersQuery.data;
  const settingsQuery = useSettingsQuery();
  const settings = settingsQuery.data;

  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [advOpen, setAdvOpen] = useState(false);
  const [submitted, setSubmitted] = useState<AskOptions | null>(null);
  // `seeded` is what stops the two effects below from fighting the user: once
  // the saved defaults have been applied, a later refetch must not reach in
  // and undo a filter somebody has since changed by hand.
  const [seeded, setSeeded] = useState(false);
  const [options, setOptions] = useState<AskOptions>({
    features: DEFAULT_FEATURES,
    tags: [],
    audiences: [],
    noYear: true,
    noTags: true,
  });

  // The year range comes from the library, so the slider spans what actually
  // exists rather than an arbitrary window.
  useEffect(() => {
    if (!filters) return;
    setOptions((o) => ({
      ...o,
      yearStart: o.yearStart ?? filters.yearRange.oldest,
      yearEnd: o.yearEnd ?? filters.yearRange.newest,
    }));
  }, [filters]);

  // My Settings, applied once. Tags are left unselected unless the person saved
  // some: an empty tag filter means "no filter", and pre-selecting every tag
  // would look identical while behaving differently the moment a document is
  // added with a topic nobody has curated yet.
  useEffect(() => {
    if (!settings || seeded) return;
    setSeeded(true);
    setAdvOpen(settings.advancedOpen);
    setOptions((o) => ({
      ...o,
      tags: settings.defaultTags.length > 0 ? settings.defaultTags : o.tags,
      audiences: settings.defaultAudiences.length > 0 ? settings.defaultAudiences : o.audiences,
      noYear: settings.defaultNoYear,
      noTags: settings.defaultNoTags,
    }));
  }, [settings, seeded]);

  const autoTitle = options.features.includes("auto_title");
  const canSubmit = question.trim().length > 0 && (autoTitle || title.trim().length > 0);
  const emptyLibrary = filters !== undefined && filters.documents === 0;

  return (
    <Page>
      <PageHeader
        title="What would you like to know?"
        description="Ask a question grounded in your document library. Every claim in the answer is cited."
      />

      <PageScroll>
        {/*
          Two columns: the question on the left, what it will be asked against
          on the right. They were stacked, which put the filters below the Ask
          button — so the control that decides which half of the library is
          searched sat underneath the control that searches it, and on a wide
          screen both were a narrow ribbon down the middle of the window.

          The pair is capped and centred rather than fluid. Everything else in
          the app is a table or a dashboard and fills whatever it is given;
          this page is one textarea, and a text field three thousand pixels
          wide is not a better text field.
        */}
        <div className="mx-auto w-full gap-4 xl:grid xl:grid-cols-[minmax(0,54rem)_24rem] xl:items-start xl:justify-center">
          <div className="flex flex-col gap-4">
            {/* Saying so here costs nothing and saves a ninety-second wait that can
                only end in "no relevant documents". */}
            {emptyLibrary && (
              <div className="rounded-card border border-amber/40 bg-amber-bg px-4 py-3 text-sm text-amber-text">
                No documents have finished processing yet, so there is nothing to ground an answer
                in. Check the Library for documents still being indexed.
              </div>
            )}

            <Card className="p-5">
              {/* Revealed by turning *Generate Title* off, exactly as ERIKA did.
                  Until the feature checklist existed this input could never render,
                  which meant a user could never title their own question. */}
              {!autoTitle && (
                <div className="mb-4">
                  <label className="mb-1 block text-sm font-medium" htmlFor="question-title">
                    Question title (required)
                  </label>
                  <Input
                    id="question-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter a title to be saved with the question"
                  />
                </div>
              )}

              <Textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                placeholder="Enter a question"
                rows={10}
                maxLength={8000}
                className="min-h-[12rem]"
              />

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-border pt-4">
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox checked={isPrivate} onCheckedChange={(c) => setIsPrivate(c === true)} />
                  Question is private
                  <span className="text-xs">
                    {isPrivate ? "Only you will see it" : "Colleagues can read it"}
                  </span>
                </label>
                <Button
                  variant="primary"
                  disabled={!canSubmit || submitted !== null}
                  onClick={() =>
                    // The three always-on stages are re-added on the way out rather
                    // than trusted to still be in state. They are rendered disabled,
                    // so nothing in this page can have removed one — but a request
                    // that silently skipped retrieval because of a UI bug would
                    // produce an ungrounded answer, and that is not a failure mode
                    // worth leaving to a rendering detail.
                    setSubmitted({
                      ...options,
                      features: Array.from(new Set([...options.features, ...LOCKED_FEATURES])),
                      // A slider left at its start is no lower bound at all.
                      // The range leaves out ERIKA's year-0 documents, and
                      // sending its start would quietly drop them from every
                      // question that never touched the years.
                      yearStart:
                        filters && options.yearStart !== undefined && options.yearStart <= filters.yearRange.oldest
                          ? undefined
                          : options.yearStart,
                    })
                  }
                >
                  Ask question
                </Button>
              </div>
            </Card>

            <p className={`text-xs text-chrome/50 ${MEASURE}`}>
              Answers take 30&ndash;90 seconds. The question is saved as soon as you ask it, so you
              can leave this page and pick the answer up below.
            </p>

            <MyRecentQuestions />
          </div>

          {/* The same panel, in the rail rather than under the button. My
              Settings' "start Advanced Options open" still decides the
              stacked layout, which is the one it was written for. */}
          <Rail className="mt-4 xl:mt-0">
            {filters && (
              <AdvancedOptions
                open={advOpen}
                onOpenChange={setAdvOpen}
                collapsible={!wide}
                filters={filters}
                options={options}
                onOptionsChange={setOptions}
              />
            )}
          </Rail>
        </div>
      </PageScroll>

      {submitted && (
        <ProcessingModal
          question={question}
          title={title}
          isPrivate={isPrivate}
          options={submitted}
          onClose={() => setSubmitted(null)}
          onDone={(questionId) => {
            // Navigating to the saved question rather than rendering the
            // answer inline means a reload, a bookmark and a shared link all
            // land on the same thing.
            void navigate({ to: "/questions/$questionId", params: { questionId } });
          }}
        />
      )}
    </Page>
  );
}
